import { compact } from 'lodash-es';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';

const TYPE_CATEGORY = {
  TRUCK: 'truck',
  ELECTRIC: 'electric',
};

export const KEY_TO_HASH = {
  driving: MENU_HASH.DRIVING,
  comfort: MENU_HASH.COMFORT,
  interior: MENU_HASH.INTERIOR,
  technology: MENU_HASH.TECHNOLOGY,
  utility: MENU_HASH.UTILITY,
  mpg: MENU_HASH.MPG,
  storage: MENU_HASH.STORAGE,
  value: MENU_HASH.VALUE,
  wildcard: MENU_HASH.WILDCARD,
  reviewSafety: MENU_HASH.REVIEW_SAFETY,
  inCabinTech: MENU_HASH.TECHNOLOGY,
  storageCargoTowing: MENU_HASH.STORAGE,
  goodValue: MENU_HASH.VALUE,
  trucksTowHaulValue: MENU_HASH.TOW_HAUL,
  mpgOrRange: MENU_HASH.MPG,
  xFactor: MENU_HASH.X_FACTOR,
};

const SECTION_MAP = {
  DRIVING: { name: 'Performance', question: 'How does it drive?', key: 'driving' },
  COMFORT: { name: 'Comfort', question: 'How comfortable is it?', key: 'comfort' },
  INTERIOR: { name: 'Interior', question: 'How’s the interior?', key: 'interior' },
  TECHNOLOGY: { name: 'Technology', question: 'How’s the tech?', key: 'technology' },
  UTILITY: { name: 'Storage', question: 'How’s the storage?', key: 'utility' },
  MPG: { name: 'Fuel Economy', question: 'How’s the fuel economy?', key: 'mpg' },
  VALUE: { name: 'Value', question: 'Is it a good value?', key: 'value' },
  WILDCARD: { name: 'Wildcard', key: 'wildcard' },
};

function getModelSectionMapDefault({ modelName, edmundsTypeCategories }) {
  const typeCategories = edmundsTypeCategories ? Object.keys(edmundsTypeCategories) : [];
  const modelSectionMap = {
    ...SECTION_MAP,
    DRIVING: { ...SECTION_MAP.DRIVING, question: `How does the ${modelName} drive?` },
    COMFORT: { ...SECTION_MAP.COMFORT, question: `How comfortable is the ${modelName}?` },
    VALUE: { ...SECTION_MAP.VALUE, question: `Is the ${modelName} a good value?` },
  };

  if (typeCategories.includes(TYPE_CATEGORY.TRUCK)) {
    modelSectionMap.UTILITY = {
      ...SECTION_MAP.UTILITY,
      name: 'Towing and Storage',
      question: 'How are the towing and storage?',
    };
  }

  if (typeCategories.includes(TYPE_CATEGORY.ELECTRIC)) {
    modelSectionMap.MPG = {
      ...SECTION_MAP.MPG,
      name: 'Range and Efficiency',
      question: 'How are the range and efficiency?',
    };
  }

  return [
    modelSectionMap.DRIVING,
    modelSectionMap.COMFORT,
    modelSectionMap.INTERIOR,
    modelSectionMap.TECHNOLOGY,
    modelSectionMap.UTILITY,
    modelSectionMap.MPG,
    modelSectionMap.VALUE,
    modelSectionMap.WILDCARD,
  ];
}

const SECTION_MAP_V4 = {
  DRIVING: { name: 'Driving experience', question: 'How does it drive?', key: 'driving' },
  COMFORT: { name: 'Comfort', question: 'How comfortable is it?', key: 'comfort' },
  IN_CABIN_TECH: { name: 'In-cabin tech', question: 'How’s the tech?', key: 'inCabinTech' },
  STORAGE_CARGO_TOWING: { name: 'Storage & cargo', question: 'How’s storage and cargo?', key: 'storageCargoTowing' },
  GOOD_VALUE: { name: 'Value', question: 'Is it a good value?', key: 'goodValue' },
  TRUCKS_TOW_HAUL_VALUE: {
    name: 'Towing & hauling',
    question: 'How is the towing and hauling?',
    key: 'trucksTowHaulValue',
  },
  MPG_OR_RANGE: { name: 'MPG', question: 'How’s the fuel economy?', key: 'mpgOrRange' },
  X_FACTOR: { name: 'X factor', key: 'xFactor' },
};

function getModelSectionMapV4({ makeName, modelName, params }) {
  const modelSectionMap = {
    ...SECTION_MAP_V4,
    DRIVING: { ...SECTION_MAP_V4.DRIVING, question: `How does the ${makeName} ${modelName} drive?` },
    COMFORT: { ...SECTION_MAP_V4.COMFORT, question: `How comfortable is the ${makeName} ${modelName}?` },
    GOOD_VALUE: { ...SECTION_MAP_V4.GOOD_VALUE, question: `Is the ${makeName} ${modelName} a good value?` },
  };

  if (!params?.isTruck) {
    delete modelSectionMap.TRUCKS_TOW_HAUL_VALUE;
  }

  if (params?.isElectric) {
    modelSectionMap.MPG_OR_RANGE = {
      ...SECTION_MAP_V4.MPG_OR_RANGE,
      name: 'Range/Efficiency/Charging',
      question: 'How are the range and efficiency?',
    };
  }

  if (params?.isPluginHybrid) {
    modelSectionMap.MPG_OR_RANGE = { ...SECTION_MAP_V4.MPG_OR_RANGE, name: 'Electric range/MPG' };
  }

  return compact([
    modelSectionMap.DRIVING,
    modelSectionMap.COMFORT,
    modelSectionMap.IN_CABIN_TECH,
    modelSectionMap.STORAGE_CARGO_TOWING,
    modelSectionMap.GOOD_VALUE,
    modelSectionMap.TRUCKS_TOW_HAUL_VALUE,
    modelSectionMap.MPG_OR_RANGE,
    modelSectionMap.X_FACTOR,
  ]);
}

export function getModelSectionMap({ params, makeName, modelName, edmundsTypeCategories, isScorecardV4 }) {
  if (isScorecardV4) {
    return getModelSectionMapV4({ makeName, modelName, params });
  }

  return getModelSectionMapDefault({ modelName, edmundsTypeCategories });
}

const MPG_V4_TRANSLATED = {
  ELECTRIC: { name: 'Range/Efficiency/Charging', question: 'How are the range and efficiency?' },
  GAS: { name: 'MPG', question: 'How’s the fuel economy?' },
  PHEV: { name: 'Electric range/MPG', question: 'How’s the fuel economy?' },
};
const SECTION_MAP_V4_TRANSLATED = {
  driving: { name: 'Driving experience', question: 'How does it drive?', key: 'driving' },
  comfort: { name: 'Comfort', question: 'How comfortable is it?' },
  tech: { name: 'In-cabin tech', question: 'How’s the tech?' },
  storage: { name: 'Storage & cargo', question: 'How’s storage and cargo?' },
  value: { name: 'Value', question: 'Is it a good value?' },
  truck: { name: 'Towing & hauling', question: 'How is the towing and hauling?' },
  'x-factor': { name: 'X factor' },
};

export function addRatingsV4NamesAndQuestions({ sections, params }) {
  if (!sections?.length) {
    return sections;
  }

  const sectionMap = { ...SECTION_MAP_V4_TRANSLATED, mpg: MPG_V4_TRANSLATED.GAS };
  if (params?.isElectric) {
    sectionMap.mpg = MPG_V4_TRANSLATED.ELECTRIC;
  }
  if (params?.isPluginHybrid) {
    sectionMap.mpg = MPG_V4_TRANSLATED.PHEV;
  }

  return sections.map(section => {
    const sectionData = sectionMap?.[section.key];

    return {
      ...section,
      question: sectionData.question,
      name: sectionData.name,
    };
  });
}
